import React, { useEffect, useState, useRef } from 'react';
import { specificThemeRender } from '@/utils/specificThemeRender';
import { getThemeComponent } from '@/utils/themeImports';
import { useRouter } from 'next/router';
import { useSSRSelector } from '@/redux/ssrStore';
import { getStorePolicies } from '@/redux/actions';
import { useDispatch, useSelector } from 'react-redux';

const IS_SERVER = typeof window === 'undefined';

function StorePolicy() {
  const router = useRouter();
  let lastTimeout;
  const policyRef = useRef();
  const dispatch = useDispatch();

  const { storePolicies } = useSelector((state) => ({
    storePolicies: state.storePolicyReducer.data,
  }));

  const { storeData } = useSSRSelector((state) => ({
    storeData: state.storeReducer.store,
  }));

  const [selectedPage, setSelectedPage] = useState(1);

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  };

  /**
   * function to scroll to specifc element if element
   * with specific data-id exists
   * @param {*} eleId
   */
  const specificElementToScroll = (eleId) => {
    const specificElementBasedOnNavId = document.querySelector(`[data-id='${eleId}']`);
    if (specificElementBasedOnNavId) {
      window.scrollTo({
        top: specificElementBasedOnNavId.offsetTop - 90,
        left: 0,
        behavior: 'instant',
      });
    }
  };

  useEffect(() => {
    if (!storePolicies?.length && storeData?.store_id) {
      dispatch(getStorePolicies({ store_id: storeData.store_id }));
    }
  }, [storeData?.store_id, storePolicies?.length]);

  /**
   * This useEffect is used to go to corresponding
   * policy heading that is clicked from footer
   */
  useEffect(() => {
    if (!IS_SERVER && router?.query?.page_type && storePolicies?.length) {
      setSelectedPage(Number(router?.query?.page_type));
      specificElementToScroll(Number(router?.query?.page_type));
    }
  }, [IS_SERVER, router?.query, storePolicies]);

  useEffect(() => {
    if (storePolicies.length > 0) {
      const observer = new IntersectionObserver(observerCallback, options);
      function observerCallback(entries, observer) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            /**
             * timeOut is used here to avoid conflict of functionality
             * with the onclick handler of policy navigation
             */
            if (lastTimeout) clearTimeout(lastTimeout);
            lastTimeout = setTimeout(function () {
              setSelectedPage(Number(entry?.target?.dataset?.id));
            }, 10);
          }
        });
      }
      policyRef.current.childNodes.forEach((ele) => {
        const header = ele.querySelector('.page-heading');
        observer.observe(header);
      });
    }
  }, [storePolicies]);

  /**
   * function to go to specifc policy header
   * that is clicked
   * @param {*} e
   */
  const handleNavbarItemClick = (e) => {
    const targetId = e.target.getAttribute('id');
    if (targetId) {
      specificElementToScroll(Number(targetId));
      setSelectedPage(Number(targetId));
    }
  };

  const calculateOffsetTop = () => {
    let header = null;
    if (document) {
      const parentElement = document.getElementsByClassName('store-policy-page')[0];
      header = parentElement?.firstChild;
    }

    return header ? `${header?.offsetHeight || 0}px` : 0;
  };

  if (IS_SERVER) return null;

  return (
    <section className="store-policy-page">
      {specificThemeRender(
        storeData?.theme,
        storeData?.store_premium_theme,
        storeData?.premium
      )?.components?.Header?.map((layout) => {
        return getThemeComponent(layout.sub_type, layout.name, layout);
      })}
      <div className="store-policies">
        <div
          className="policy-navbar"
          style={{
            top: calculateOffsetTop(),
          }}
        >
          {storePolicies?.length > 0 && (
            <ul className="">
              {storePolicies.map((page) => {
                return (
                  <li
                    key={page.id}
                    id={page.page_type}
                    className={`${selectedPage == page.page_type ? 'page-selected' : ''}`}
                    onClick={(e) => handleNavbarItemClick(e)}
                  >
                    {page.page_name}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="policy-page mb3" ref={policyRef}>
          {storePolicies?.length > 0 &&
            storePolicies?.map((page) => {
              return (
                <div className="policy-component" key={page.id}>
                  <div className="page-heading" data-id={page.page_type}>
                    <h3 className="page-heading-text">{page.page_name}</h3>
                    <p className="updated-at-text">
                      Updated at {page.updated_at.substr(0, 10)}
                    </p>
                  </div>
                  <div
                    className="page-content"
                    dangerouslySetInnerHTML={{ __html: page.html }}
                  ></div>
                  <br />
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
}
export default StorePolicy;
